import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { motion } from 'framer-motion'
import { saveTrialToProject } from '@/services/trialService'

type Language = 'nl' | 'en'

const translations = {
  nl: {
    title: 'Stel je wachtwoord in',
    subtitle: 'Kies een veilig wachtwoord om je account te beveiligen.',
    password: 'Wachtwoord',
    passwordPlaceholder: 'Minimaal 8 karakters',
    confirmPassword: 'Bevestig wachtwoord',
    confirmPlaceholder: 'Herhaal je wachtwoord',
    submit: 'Wachtwoord instellen',
    loading: 'Even geduld...',
    success: 'Wachtwoord ingesteld!',
    successMessage: 'Je wordt doorgestuurd naar het dashboard...',
    errors: {
      passwordRequired: 'Wachtwoord is verplicht',
      passwordMin: 'Wachtwoord moet minimaal 8 karakters bevatten',
      passwordMismatch: 'Wachtwoorden komen niet overeen',
      invalidToken: 'Deze link is verlopen of ongeldig. Gebruik de "Wachtwoord vergeten" optie op de inlogpagina.',
      generic: 'Er is een fout opgetreden. Probeer het opnieuw.',
    },
    requirements: {
      title: 'Wachtwoord vereisten:',
      min: 'Minimaal 8 karakters',
      match: 'Wachtwoorden moeten overeenkomen',
    },
  },
  en: {
    title: 'Set your password',
    subtitle: 'Choose a secure password to protect your account.',
    password: 'Password',
    passwordPlaceholder: 'Minimum 8 characters',
    confirmPassword: 'Confirm password',
    confirmPlaceholder: 'Repeat your password',
    submit: 'Set password',
    loading: 'Please wait...',
    success: 'Password set!',
    successMessage: 'Redirecting to dashboard...',
    errors: {
      passwordRequired: 'Password is required',
      passwordMin: 'Password must be at least 8 characters',
      passwordMismatch: 'Passwords do not match',
      invalidToken: 'This link has expired or is invalid. Use the "Forgot password" option on the login page.',
      generic: 'An error occurred. Please try again.',
    },
    requirements: {
      title: 'Password requirements:',
      min: 'Minimum 8 characters',
      match: 'Passwords must match',
    },
  },
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
  },
}

export function SetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [isValidSession, setIsValidSession] = useState<boolean | null>(null)
  const [language] = useState<Language>('en')
  const [trialConversionInProgress, setTrialConversionInProgress] = useState(false)

  // Get trial sessionId from URL params (for trial-to-account conversion)
  const sessionId = searchParams.get('sessionId')

  const t = translations[language]

  useEffect(() => {
    // Check for errors in URL hash (expired link, etc.)
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const errorParam = hashParams.get('error')
    const errorDescription = hashParams.get('error_description')

    // Check if this is a recovery flow by looking at URL parameters
    // Supabase includes type=recovery in the URL for password reset links
    const urlType = hashParams.get('type')
    const isRecoveryFlow = urlType === 'recovery'


    if (errorParam) {
      console.error('[SetPassword] Auth error:', errorParam, errorDescription)
      setError(t.errors.invalidToken)
      setIsValidSession(false)
      return
    }

    let hasReceivedRecoveryEvent = false

    // Listen for auth state changes (recovery link creates a session)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {

      // PASSWORD_RECOVERY is the key event for password reset
      if (event === 'PASSWORD_RECOVERY') {
        hasReceivedRecoveryEvent = true
        if (session?.user) {
          setEmail(session.user.email || '')
          setIsValidSession(true)
        }
      }
      // SIGNED_IN can occur from a recovery flow, but only trust it if:
      // 1. We've already seen PASSWORD_RECOVERY, or
      // 2. The URL indicates this is a recovery flow
      else if (event === 'SIGNED_IN') {
        if ((hasReceivedRecoveryEvent || isRecoveryFlow) && session?.user) {
          setEmail(session.user.email || '')
          setIsValidSession(true)
        } else {
          // This is a regular sign-in, not a password recovery
          // User might have arrived here by mistake or with an expired link
        }
      }
    })

    // Also check current session
    supabase.auth.getSession().then(({ data: { session } }) => {

      if (session?.user) {
        // We have a session. If this looks like a recovery flow (from URL), allow it
        if (isRecoveryFlow) {
          setEmail(session.user.email || '')
          setIsValidSession(true)
        } else {
          // User has an active session but this doesn't look like a recovery flow
          // They may have navigated here directly - redirect to dashboard
        }
      } else {
        // Give it a moment for the auth state to update from URL hash
        setTimeout(() => {
          supabase.auth.getSession().then(({ data: { session: s } }) => {
            if (s?.user) {
              // Session appeared - check if recovery flow
              if (isRecoveryFlow || hasReceivedRecoveryEvent) {
                setEmail(s.user.email || '')
                setIsValidSession(true)
              } else {
                // Not a recovery flow
                setIsValidSession(false)
                setError(t.errors.invalidToken)
              }
            } else {
              setIsValidSession(false)
              setError(t.errors.invalidToken)
            }
          })
        }, 1000)
      }
    })

    return () => subscription.unsubscribe()
  }, [t.errors.invalidToken])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!password) {
      setError(t.errors.passwordRequired)
      return
    }

    if (password.length < 8) {
      setError(t.errors.passwordMin)
      return
    }

    if (password !== confirmPassword) {
      setError(t.errors.passwordMismatch)
      return
    }

    setIsLoading(true)

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      })

      if (updateError) {
        throw updateError
      }

      setIsSuccess(true)

      // If we have a trial sessionId, convert it to a project
      if (sessionId) {
        setTrialConversionInProgress(true)

        // Get the current user
        const {
          data: { user },
        } = await supabase.auth.getUser()

        if (user) {
          const result = await saveTrialToProject(sessionId, user.id)

          setTrialConversionInProgress(false)

          if (result.success && result.projectId) {
            // Redirect to the converted project
            setTimeout(() => {
              navigate(`/project/${result.projectId}?view=discovery`)
            }, 1500)
            return
          } else {
            console.error('[SetPassword] Trial conversion failed:', result.error)
            setError(result.error || 'Failed to create your project. You can try again from the dashboard.')
          }
        }
      }

      // Default: redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard')
      }, 2000)
    } catch (err: any) {
      console.error('Error setting password:', err)
      setError(t.errors.generic)
    } finally {
      setIsLoading(false)
    }
  }

  const isPasswordValid = password.length >= 8
  const doPasswordsMatch = password === confirmPassword && confirmPassword.length > 0

  // Still loading
  if (isValidSession === null) {
    return (
      <div className="min-h-screen bg-[#09090b] text-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-4" />
          <p className="text-zinc-400">Loading...</p>
        </div>
      </div>
    )
  }

  // Invalid session/token
  if (isValidSession === false) {
    return (
      <div className="min-h-screen bg-[#09090b] text-white antialiased">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-red-500/10 rounded-full blur-[120px]" />
        </div>

        <div className="relative min-h-screen flex items-center justify-center px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full text-center"
          >
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold mb-4">Invalid link</h1>
            <p className="text-zinc-400 mb-8">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl transition-colors"
            >
              Go to login page
            </button>
          </motion.div>
        </div>
      </div>
    )
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-[#09090b] text-white antialiased">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[120px]" />
        </div>

        <div className="relative min-h-screen flex items-center justify-center px-6 py-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md w-full text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/30"
            >
              {trialConversionInProgress ? (
                <Loader2 className="w-10 h-10 text-white animate-spin" />
              ) : (
                <CheckCircle className="w-10 h-10 text-white" />
              )}
            </motion.div>
            <h1 className="text-3xl font-bold mb-3">{t.success}</h1>
            <p className="text-zinc-400">
              {trialConversionInProgress
                ? (language === 'nl' ? 'Project wordt aangemaakt...' : 'Creating your project...')
                : t.successMessage}
            </p>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#09090b] text-white antialiased">
      {/* Ambient background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-framed-yellow-500/10 rounded-full blur-[100px]" />
      </div>

      {/* Subtle grid pattern */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.015]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '64px 64px',
        }}
      />

      <div className="relative min-h-screen flex items-center justify-center px-6 py-20">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-md w-full"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-10">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-amber-500/30">
              <Lock className="w-8 h-8 text-black" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-3">{t.title}</h1>
            <p className="text-zinc-400">
              {t.subtitle}
              {email && <span className="block text-sm mt-2 text-zinc-500">{email}</span>}
            </p>
          </motion.div>

          {/* Form */}
          <motion.form variants={itemVariants} onSubmit={handleSubmit} className="space-y-6">
            {/* Error message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-500/10 border border-red-500/20 text-red-400 px-5 py-4 rounded-2xl flex items-start gap-3"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </motion.div>
            )}

            {/* Password field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-zinc-300 mb-2">
                {t.password}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-zinc-500" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-12 pr-12 py-4 bg-zinc-900/50 border border-white/10 hover:border-white/20 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 text-white placeholder-zinc-500 transition-all"
                  placeholder={t.passwordPlaceholder}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm password field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-zinc-300 mb-2">
                {t.confirmPassword}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-zinc-500" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full pl-12 pr-4 py-4 bg-zinc-900/50 border border-white/10 hover:border-white/20 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 text-white placeholder-zinc-500 transition-all"
                  placeholder={t.confirmPlaceholder}
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password requirements */}
            <div className="bg-zinc-900/30 border border-white/5 rounded-xl p-4">
              <p className="text-sm font-medium text-zinc-400 mb-3">{t.requirements.title}</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${isPasswordValid ? 'bg-emerald-500/20' : 'bg-zinc-800'}`}>
                    {isPasswordValid && <CheckCircle className="w-3 h-3 text-emerald-400" />}
                  </div>
                  <span className={isPasswordValid ? 'text-emerald-400' : 'text-zinc-500'}>{t.requirements.min}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${doPasswordsMatch ? 'bg-emerald-500/20' : 'bg-zinc-800'}`}>
                    {doPasswordsMatch && <CheckCircle className="w-3 h-3 text-emerald-400" />}
                  </div>
                  <span className={doPasswordsMatch ? 'text-emerald-400' : 'text-zinc-500'}>{t.requirements.match}</span>
                </div>
              </div>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading || !isPasswordValid || !doPasswordsMatch}
              className="w-full py-4 px-6 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 disabled:from-amber-500/50 disabled:to-amber-600/50 text-black font-semibold rounded-2xl transition-all duration-300 flex items-center justify-center gap-3 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 disabled:shadow-none disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t.loading}</span>
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  <span>{t.submit}</span>
                </>
              )}
            </button>
          </motion.form>
        </motion.div>
      </div>
    </div>
  )
}
